import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import React, { useContext, useEffect, useMemo } from 'react'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import MetaSearchTile from '../MetaSearchTile'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import BedbankSearchTileFeatures from './BedbankSearchTileFeatures'
import BedbankMetaSearchTilePriceStack from './BedbankMetaSearchTilePriceStack'
import BedbankSearchTileUnavailabilityBanner from './BedbankSearchTileUnavailabilityBanner'
import { generalLog } from 'marketing/api'
import BedbankMetaSearchTileAction from './BedbankMetaSearchTileAction'
import { selectLoggedIn } from 'selectors/accountSelectors'
import Label from 'LuxKit/Label'

const ALLOWED_PRICE_DIFFERENCE = 2

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function BedbankMetaSearchTile({ offer, filters }: Props) {
  const utmSource = useAppSelector(state => state.utm.source)
  const experimentsSearchTileCompactFeatures = !!useOptimizelyExperiment(
    OptimizelyExperiments.searchTileCompactFeatures,
    utmSource !== 'google'
  )
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)
  const locations = useMemo<Array<string>>(() => {
    if (offer.property.locationTitleOverride) return [offer.property.locationTitleOverride]

    return [
      [offer.property.address.city, offer.property.address.countryName].filter(Boolean).join(', '),
    ]
  }, [offer])

  const mapUrl = useMapSearchOfferUrl(offer)

  const utmBp = useAppSelector(state => state.utm.bp)

  const shouldUseSearchPrices = getShouldUseSearchPrices(filters, true)

  const offerMetaData = useOfferMetaData(offer.id, filters)

  const shouldForceFetchRate = !!(filters?.checkIn && filters?.checkOut)
  const {
    offerPrice,
    shownRate,
    fetchingRates,
  } = useBedbankOfferSearchPricing(offer, filters, undefined, shouldForceFetchRate)

  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer.property.timezone])

  const hasDates = !!(filters?.checkIn && filters.checkOut)
  const isSoldOut = shouldUseSearchPrices ? !offerMetaData?.available : (!shownRate && hasDates && !fetchingRates)

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        availability: false,
        extendedAvailability: offerMetaData?.extendedAvailability,
        price: undefined,
        memberPrice: undefined,
        duration: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  useEffect(() => {
    if (offerPrice) {
      const priceTotal = queryParams.priceTotal || 0
      generalLog({
        status: 'info',
        message: 'GoogleHotelSearchPage: highlightedPackage',
        payload: { ...queryParams, fullPath, priceReceived: offerPrice, priceDifference: Number(priceTotal) - offerPrice },
      })
    }
  }, [offerPrice, queryParams, fullPath])

  const hidePricing = !!offerMetaData?.hidePricing
  const primaryFilters = useMemo(() => ({ ...filters, source: 'metasearch' }), [filters])
  const secondaryFilters = useMemo(() => ({ ...filters, source: utmBp === '2' ? 'loggedInMetasearch' : undefined }), [filters, utmBp])

  const primaryPrice = useBedbankOfferSearchPricing(offer, primaryFilters)
  const secondaryPrice = useBedbankOfferSearchPricing(offer, secondaryFilters)

  // Allow a small difference in price for metasearch tiles
  const isSamePrice = Math.abs(primaryPrice.offerPrice - secondaryPrice.offerPrice) < ALLOWED_PRICE_DIFFERENCE
  const showPrimaryPrice = !isLoggedIn && !isSamePrice
  const hideSecondaryPrice = !isLoggedIn && showPrimaryPrice && !filters?.checkIn && !filters?.checkOut && utmBp === '2'

  return <MetaSearchTile
    state="default"
    actionPrimary={!isSoldOut && <BedbankMetaSearchTileAction offer={offer} filters={filters} />}
    actionSecondary={!isSoldOut && <BedbankMetaSearchTileAction offer={offer} filters={filters} utmBp={utmBp} kind="secondary" />}
    bookmarkAction={<BookmarkButton offer={offer} />}
    cancellationPolicyLabel={(!experimentsSearchTileCompactFeatures && offer.productType === 'limited_time_special' && (shownRate || cancellationPolicy)) && <BedbankCancellationPolicy
      rate={shownRate ?? cancellationPolicy}
      showNonCancellation
    />}
    bottomExtension={isSoldOut &&
      <BedbankSearchTileUnavailabilityBanner
        offer={offer}
        unavailableReason={offerMetaData?.unavailableReason}
        filters={filters}
      />}
    // Hiding luxplus price and banners for metasearchtiles
    features={<BedbankSearchTileFeatures offer={offer} filters={{ ...filters, isLuxPlusMember: false }} singleColumn={showPrimaryPrice && !hideSecondaryPrice} truncateAfter={showPrimaryPrice && !hideSecondaryPrice ? 3 : 6} />}
    images={offer.images}
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={mapUrl} />}
    primaryPricePoint={!isSoldOut && <BedbankMetaSearchTilePriceStack offer={offer} filters={primaryFilters} hidePrice={hidePricing} />}
    secondaryPricePoint={!isSoldOut && <BedbankMetaSearchTilePriceStack offer={offer} filters={secondaryFilters} hidePrice={hidePricing} />}
    showPrimaryPrice={showPrimaryPrice}
    hideSecondaryPrice={hideSecondaryPrice}
    productLabel={<Label kind="primary" variant="highlight">You searched for this property</Label>}
    title={offer.name}
    offer={offer}
  />
}

export default BedbankMetaSearchTile
